import { useMutation } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import type { LabelScope, StandardLabel, ContentType } from '@system-config/label-management/domain';
import { UPDATE_STANDARD_LABEL, GET_STANDARD_LABELS } from '@system-config/label-management/graphql';
import { useState, useCallback } from 'react';

export interface UpdateStandardLabelInput {
  labelName?: string;
  labelShortName?: string;
  policy?: string;
  contentType?: ContentType;
  labelNumMap?: number;
  labelStatus?: string;
  scope?: LabelScope;
}

interface UpdateStandardLabelResponse {
  updateStandardLabel: {
    label: StandardLabel;
  };
}

export function useUpdateStandardLabel() {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [updateStandardLabelMutation, { loading }] = useMutation<UpdateStandardLabelResponse>(UPDATE_STANDARD_LABEL);

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const updateStandardLabel = async (id: string, input: UpdateStandardLabelInput): Promise<boolean> => {
    try {
      track('UpdateStandardLabelInitiated', {
        labelId: id,
      });

      await updateStandardLabelMutation({
        variables: { id, input },
        refetchQueries: [{ query: GET_STANDARD_LABELS }],
        awaitRefetchQueries: true,
      });

      track('UpdateStandardLabelSucceeded', {
        labelId: id,
      });

      return true;
    } catch (error) {
      track('UpdateStandardLabelFailed', {
        labelId: id,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    updateStandardLabel,
    loading,
    errors,
    resetErrors,
  };
}

export default useUpdateStandardLabel;
